import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import FlairCard from '../../../../atomic/templates/FlairCard/FlairCard';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import HubElementIcon from '../../components/HubElementIcon/HubElementIcon';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import {
  HubElement,
  HubElementContentType,
  useHubElements,
} from '../../hooks/useHubElements';
import { useHubElementHandler } from '../../hooks/useHubElementHandler';
import './HubElementGroupPage.css';

const i18Path = 'dashboard.hubElements';

const HubElementGroupPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const t = useNamespacedTranslation(i18Path);
  const { loading, byId, getGroupChildren } = useHubElements();
  const onActivate = useHubElementHandler();

  if (loading) {
    return null;
  }

  const parent = groupId ? byId.get(groupId) : undefined;
  if (!parent) {
    return (
      <Container>
        <PageHeader title={t('groupNotFound')} />
      </Container>
    );
  }

  const children = getGroupChildren(parent.Id);
  const title =
    parent.flair__Menu_Item_Name__c || parent.Name || t('untitledGroup');

  return (
    <Container>
      <PageHeader title={title} />
      {children.length === 0 ? (
        <FlairCard>{t('emptyGroup')}</FlairCard>
      ) : (
        <Row className="g-3">
          {children.map((child) => (
            <Col key={child.Id} xs={12} sm={6} lg={4}>
              <HubElementCard element={child} onClick={onActivate} />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

type HubElementCardProps = {
  element: HubElement;
  onClick: (el: HubElement) => void;
};

const HubElementCard: React.FC<
  React.PropsWithChildren<HubElementCardProps>
> = ({ element, onClick }) => {
  const label = element.flair__Menu_Item_Name__c || element.Name || '';
  const isExternal =
    element.config.contentType === HubElementContentType.ExternalUrl;

  return (
    <button
      type="button"
      className="hub-element-card-button w-100 text-start"
      onClick={() => onClick(element)}>
      <FlairCard>
        <div className="d-flex align-items-center gap-3">
          <HubElementIcon iconValue={element.config.icon} size="lg" />
          <span className="flex-grow-1 fw-semibold">{label}</span>
          {isExternal && <FlairIcon icon="external-link-outline" size="sm" />}
        </div>
      </FlairCard>
    </button>
  );
};

export default HubElementGroupPage;
